import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AppointmentsService } from '../api/AppointmentsAPI';
import { LogService } from '../api/LogAPI';
import { addPendingPhoto, getPendingPhotos, updatePendingPhoto, deletePendingPhoto } from '../api/photoQueue';
import { useAuth } from '../store';
import { RANKINGS, CHECKLIST_STEPS } from '../data';
import { LOCATION_META, STATUS_CYCLE, PRIORITY_CYCLE, isShareDeviceOnly } from '../constants';
import { isDone, getPhotoIdentity, toDateStr, formatDisplayDate, useClock } from '../utils';
import { StaffService } from '../api/StaffAPI';

import { Header } from '../components/Header/Header';
import { StatsBar } from '../components/StatsBar/StatsBar';
import { LocationColumn } from '../components/LocationColumn/LocationColumn';
import { StaffMemberCard } from '../components/StaffMemberCard/StaffMemberCard';
import { SidebarRankings } from '../components/SidebarRankings/SidebarRankings';
import { PhotoCarouselModal } from '../components/PhotoCarouselModal/PhotoCarouselModal';
import { NextStepModal } from '../components/NextStepModal/NextStepModal';
import { CameraCapture } from '../components/CameraCapture/CameraCapture';
import { AnalyticsPanel } from '../components/AnalyticsPanel/AnalyticsPanel';

import s from '../styles/dashboard.module.css';

// How many times a photo is retried before it's dropped from the local queue
// and the failure is logged to the server.
const MAX_UPLOAD_ATTEMPTS = 3;

// Whether a staff member may work an appointment at a given location. A staff
// row stores `localtionIds` as "all" or a comma-separated list of Square
// location ids; the appointment's `location` is our internal id, bridged to a
// Square id via the squareId->name map from GET /staffs. If we can't resolve
// the location's Square id (map not loaded), we don't block.
function staffAllowedAtLocation(member, apptLocationId, locationNameById) {
  const loc = member?.localtionIds;
  if (loc === 'all') return true;
  const apptName = LOCATION_META[apptLocationId]?.name;
  const apptSquareId = apptName
    ? Object.keys(locationNameById).find(id => locationNameById[id] === apptName)
    : null;
  if (!apptSquareId) return true; // can't verify — allow rather than false-block
  if (!loc) return false;
  return loc.split(',').includes(apptSquareId);
}

export function DashboardPage() {
  const today = toDateStr(new Date());
  const { user: authUser, logout } = useAuth();
  const [selectedDate, setSelectedDate] = useState(today);
  const [localData, setLocalData]       = useState({});
  const [activeTab, setActiveTab]       = useState('queue');
  const shareDeviceOnly = isShareDeviceOnly(authUser);
  const visibleTab = shareDeviceOnly ? 'queue' : activeTab;
  const [staff, setStaff]               = useState([]);
  const [dragOverId, setDragOverId]     = useState(null);
  const [activeLocations, setActiveLocations] = useState(() => {
    try {
      const raw = sessionStorage.getItem('activeLocations');
      if (raw) return JSON.parse(raw);
    } catch {}
    return Object.keys(LOCATION_META);
  });

  const toggleLocation = useCallback((locId) => {
    setActiveLocations(prev => {
      const next = prev.includes(locId)
        ? prev.filter(id => id !== locId)
        : [...prev, locId];
      try { sessionStorage.setItem('activeLocations', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const [lightMode, setLightMode] = useState(() => {
    try {
      const raw = localStorage.getItem('lightMode');
      if (!raw) return false;
      const { value } = JSON.parse(raw);
      return value;
    } catch { return false; }
  });

  const toggleLightMode = useCallback((updater) => {
    setLightMode(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem('lightMode', JSON.stringify({ value: next }));
      } catch {}
      return next;
    });
  }, []);

  // Whether the STAFF sidebar is collapsed. Persisted like the light-mode
  // preference so it survives reloads.
  const [staffMinimized, setStaffMinimized] = useState(() => {
    try {
      const raw = localStorage.getItem('staffPanelMinimized');
      if (!raw) return false;
      const { value } = JSON.parse(raw);
      return value;
    } catch { return false; }
  });

  const toggleStaffMinimized = useCallback(() => {
    setStaffMinimized(prev => {
      const next = !prev;
      try { localStorage.setItem('staffPanelMinimized', JSON.stringify({ value: next })); } catch {}
      return next;
    });
  }, []);

  const [uploadStatuses, setUploadStatuses] = useState({});
  const galleryInputRef    = useRef(null);
  const currentUploadAppt  = useRef(null);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [cameraOpenAppt, setCameraOpenAppt] = useState(null);
  const [photoModalAppt, setPhotoModalAppt] = useState(null);
  const [nextStepAppt, setNextStepAppt]     = useState(null);
  const time = useClock();

  const { data: fetchedData, isFetching, dataUpdatedAt } = useQuery({
    queryKey:        ['appointments', selectedDate],
    queryFn:         () => AppointmentsService.getAppointments(selectedDate),
    refetchInterval: selectedDate === today ? 60000 : false,
    staleTime:       30000,
  });

  useEffect(() => {
    if (!fetchedData?.byLocation) return;
    setLocalData(prev => {
      const merged = {};
      for (const locId of Object.keys(fetchedData.byLocation)) {
        const freshAppts = fetchedData.byLocation[locId].appointments || [];
        const prevAppts  = prev[locId]?.appointments || [];
        merged[locId] = {
          ...fetchedData.byLocation[locId],
          appointments: freshAppts.map(f => {
            const existing = prevAppts.find(p => p.squareId === f.squareId);
            // On first load use the server's assignedStaff (from DB); once a row
            // exists locally, keep the local list so optimistic changes persist.
            return existing ? { ...f, checks: existing.checks, bay: existing.bay, assignedStaff: existing.assignedStaff || f.assignedStaff || [], priority: existing.priority || f.priority } : { ...f, assignedStaff: f.assignedStaff || [] };
          }),
        };
      }
      return merged;
    });
  }, [fetchedData]);

  // Staff roster from the Square team API (GET /staff), replacing the hardcoded
  // seed list. Fetched once; local status/assignment changes are kept in state.
  const { data: staffData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (staffData?.staffs?.length) setStaff(staffData.staffs);
  }, [staffData]);

  const byLocation = localData;
  const loading    = isFetching && Object.keys(localData).length === 0;
  const lastFetch  = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : null;

  const handleToggleCheck = useCallback(async (locId, apptId, idx, step) => {
    const appt = byLocation[locId]?.appointments?.find(a => a.id === apptId);
    if (!appt) return;

    const normalizedStep = String(step || '').trim().toLowerCase();
    if (normalizedStep === 'photos') return;

    const nextCheck = !appt.checks[idx];

    setLocalData(prev => {
      const loc = prev[locId];
      if (!loc) return prev;
      return {
        ...prev,
        [locId]: {
          ...loc,
          appointments: loc.appointments.map(a => {
            if (a.id !== apptId) return a;
            const checks = [...a.checks];
            checks[idx] = nextCheck;
            return { ...a, checks };
          }),
        },
      };
    });

    try {
      await AppointmentsService.updateCheck({
        squareId: appt.squareId || appt.id,
        step,
        check: nextCheck,
      });
    } catch (err) {
      setLocalData(prev => {
        const loc = prev[locId];
        if (!loc) return prev;
        return {
          ...prev,
          [locId]: {
            ...loc,
            appointments: loc.appointments.map(a => {
              if (a.id !== apptId) return a;
              const checks = [...a.checks];
              checks[idx] = !nextCheck;
              return { ...a, checks };
            }),
          },
        };
      });
    }
  }, [byLocation]);

  const handleTogglePriority = useCallback((locId, apptId) => {
    setLocalData(prev => { const loc = prev[locId]; if (!loc) return prev; return { ...prev, [locId]: { ...loc, appointments: loc.appointments.map(a => a.id !== apptId ? a : { ...a, priority: PRIORITY_CYCLE[a.priority] || 'medium' }) } }; });
  }, []);

  // Optimistically set an appointment's assigned-staff list in local state.
  const setApptAssigned = (prev, locId, apptId, assignedStaff) => {
    const loc = prev[locId];
    if (!loc) return prev;
    return { ...prev, [locId]: { ...loc, appointments: loc.appointments.map(a => a.id !== apptId ? a : { ...a, assignedStaff }) } };
  };

  // `staffSquareId` is the assigned team member's Square id — the value stored
  // in appointments.assignedTo.
  const handleAssignStaff = useCallback((locId, apptId, staffSquareId) => {
    const appt = byLocation[locId]?.appointments?.find(a => a.id === apptId);
    if (!appt || !staffSquareId) return;
    const current = appt.assignedStaff || [];
    if (current.includes(staffSquareId)) return;

    // Only allow staff assigned to this appointment's location.
    const member = staff.find(m => m.squareId === staffSquareId);
    if (!staffAllowedAtLocation(member, appt.location, staffData?.locations || {})) {
      const who = member ? `${member.given_name || ''} ${member.family_name || ''}`.trim() : 'This staff member';
      const where = LOCATION_META[appt.location]?.name || appt.location;
      window.alert(`${who} is not assigned to ${where}.`);
      return;
    }

    const next = [...current, staffSquareId];

    setLocalData(prev => setApptAssigned(prev, locId, apptId, next));

    AppointmentsService.updateStaffAssignment({ squareId: appt.squareId || appt.id, staffId: staffSquareId, assign: true })
      .catch(err => {
        console.error('Assign staff failed:', err?.error || err?.message || err);
        setLocalData(prev => setApptAssigned(prev, locId, apptId, current)); // rollback
      });
  }, [byLocation, staff, staffData]);

  const handleUnassignStaff = useCallback((locId, apptId, staffSquareId) => {
    const appt = byLocation[locId]?.appointments?.find(a => a.id === apptId);
    if (!appt || !staffSquareId) return;
    const current = appt.assignedStaff || [];
    if (!current.includes(staffSquareId)) return;
    const next = current.filter(id => id !== staffSquareId);

    setLocalData(prev => setApptAssigned(prev, locId, apptId, next));

    AppointmentsService.updateStaffAssignment({ squareId: appt.squareId || appt.id, staffId: staffSquareId, assign: false })
      .catch(err => {
        console.error('Unassign staff failed:', err?.error || err?.message || err);
        setLocalData(prev => setApptAssigned(prev, locId, apptId, current)); // rollback
      });
  }, [byLocation]);

  const handleCycleStatus = useCallback((memberId) => {
    setStaff(prev => prev.map(m => m.id === memberId ? { ...m, status: STATUS_CYCLE[m.status] || 'available' } : m));
  }, []);

  const handleUploadClick = useCallback((appt) => {
    currentUploadAppt.current = appt;
    const isTouchDevice = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      galleryInputRef.current?.click();
      return;
    }
    setUploadMenuOpen(true);
  }, []);

  const handlePickCamera = useCallback(() => {
    setUploadMenuOpen(false);
    setCameraOpenAppt(currentUploadAppt.current);
  }, []);

  const handlePickGallery = useCallback(() => {
    setUploadMenuOpen(false);
    galleryInputRef.current?.click();
  }, []);

  const handleViewPhotos = useCallback((appt) => {
    setPhotoModalAppt(appt);
  }, []);

  const handleNextStep = useCallback((appt) => {
    setNextStepAppt(appt);
  }, []);

  const handleConfirmNextStep = useCallback(async ({ appt, step, idx, comment, files, phase, paymentMethod }) => {
    const { idBooking, customerName, serviceLabel } = getPhotoIdentity(appt);
    const photoStepIdx = CHECKLIST_STEPS.findIndex(st => st.toLowerCase() === 'photos');

    // 1. Upload photos (if this step requires them)
    if (files && files.length > 0) {
      await AppointmentsService.uploadPhotos({ files, idBooking, customerName, serviceLabel, phase, date: selectedDate });
    }

    // 2. Mark the step complete on the server
    await AppointmentsService.updateCheck({ squareId: appt.squareId || appt.id, step, check: true, comment, paymentMethod });

    setLocalData(prev => {
      const loc = prev[appt.location];
      if (!loc) return prev;
      return {
        ...prev,
        [appt.location]: {
          ...loc,
          appointments: loc.appointments.map(a => {
            if (a.id !== appt.id) return a;
            const checks = Array.isArray(a.checks) ? [...a.checks] : [false, false, false, false, false, false];
            checks[idx] = true;
            if ((files?.length || 0) > 0 && photoStepIdx >= 0) checks[photoStepIdx] = true;
            return { ...a, checks };
          }),
        },
      };
    });
  }, [selectedDate]);

  // Mark an appointment's "photos" checklist step done and flash its status.
  const markPhotosStepDone = useCallback((meta) => {
    const photoStepIdx = CHECKLIST_STEPS.findIndex(step => step.toLowerCase() === 'photos');
    if (photoStepIdx >= 0) {
      setLocalData(prev => {
        const loc = prev[meta.location];
        if (!loc) return prev;
        return {
          ...prev,
          [meta.location]: {
            ...loc,
            appointments: loc.appointments.map(a => {
              if (a.id !== meta.apptId) return a;
              const checks = Array.isArray(a.checks) ? [...a.checks] : [false, false, false, false, false, false];
              checks[photoStepIdx] = true;
              return { ...a, checks };
            }),
          },
        };
      });
    }
    setUploadStatuses(prev => ({ ...prev, [meta.apptId]: 'done' }));
    setTimeout(() => setUploadStatuses(prev => ({ ...prev, [meta.apptId]: 'idle' })), 3000);
  }, []);

  const queueRunningRef = useRef(false);
  const processUploadQueue = useCallback(async () => {
    if (queueRunningRef.current) return;
    queueRunningRef.current = true;
    try {

      for (;;) {
        const pending = await getPendingPhotos();
        const actionable = pending.filter(p => (p.attempts || 0) < MAX_UPLOAD_ATTEMPTS);
        if (!actionable.length) break;

        for (const item of actionable) {
          setUploadStatuses(prev => ({ ...prev, [item.meta.apptId]: 'uploading' }));
          try {
            const file = new File([item.blob], item.name || `photo_${item.id}.jpg`, { type: item.blob.type || 'image/jpeg' });
            await AppointmentsService.uploadPhotos({
              files: [file],
              idBooking:    item.meta.idBooking,
              customerName: item.meta.customerName,
              serviceLabel: item.meta.serviceLabel,
              phase:        item.meta.phase || '',
              date:         item.meta.date,
            });
            await deletePendingPhoto(item.id);
            markPhotosStepDone(item.meta);
          } catch (err) {
            const attempts = (item.attempts || 0) + 1;

            // Log the reason on every failed attempt.
            LogService.create({
              type: 'error',
              statusCode: err?.statusCode ?? err?.status ?? null,
              message: `Photo upload failed (attempt ${attempts}/${MAX_UPLOAD_ATTEMPTS}) for booking ${item.meta.idBooking} (${item.meta.customerName}): ${err?.message || err?.error || 'unknown error'}`,
              category: 'photo-upload',
            }).catch(() => {});

            if (attempts >= MAX_UPLOAD_ATTEMPTS) {
              await deletePendingPhoto(item.id);
              setUploadStatuses(prev => ({ ...prev, [item.meta.apptId]: 'error' }));
              setTimeout(() => setUploadStatuses(prev => ({ ...prev, [item.meta.apptId]: 'idle' })), 3000);
            } else {
              await updatePendingPhoto({ ...item, attempts });
            }
          }
        }
      }
    } finally {
      queueRunningRef.current = false;
    }
  }, [markPhotosStepDone]);

  // Save photos locally first, then kick the background upload queue. Returning
  // once they're safely persisted means a caller (camera/gallery) can't lose a
  // photo to a flaky network — the queue owns retries from here.
  const uploadPhotos = useCallback(async (files, appt) => {
    if (!files?.length || !appt) return;

    const { idBooking, customerName, serviceLabel } = getPhotoIdentity(appt);
    if (!idBooking || !customerName) return;

    const meta = { idBooking, customerName, serviceLabel, apptId: appt.id, location: appt.location, date: selectedDate };
    setUploadStatuses(prev => ({ ...prev, [appt.id]: 'uploading' }));
    for (const file of files) {
      try {
        await addPendingPhoto({ blob: file, name: file.name, meta });
      } catch (err) {
        console.error('Failed to save photo locally:', err?.message || err);
      }
    }
    processUploadQueue(); // fire-and-forget; the queue drains in the background
  }, [selectedDate, processUploadQueue]);

  // On mount, resume any photos left in the queue from a previous session.
  useEffect(() => { processUploadQueue(); }, [processUploadQueue]);

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;
    try {
      await uploadPhotos(files, currentUploadAppt.current);
    } catch {}
  }, [uploadPhotos]);

  const allAppts  = Object.values(byLocation).flatMap(l => l.appointments || []);
  const totalDone = allAppts.filter(isDone).length;
  const isToday   = selectedDate === today;

  return (
    <div className={lightMode ? `${s.root} ${s.lightRoot}` : s.root} style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {photoModalAppt && (
        <PhotoCarouselModal appt={photoModalAppt} date={selectedDate} onClose={() => setPhotoModalAppt(null)} />
      )}
      {nextStepAppt && (
        <NextStepModal appt={nextStepAppt} onClose={() => setNextStepAppt(null)} onConfirm={handleConfirmNextStep} />
      )}
      {cameraOpenAppt && (
        <CameraCapture
          appt={cameraOpenAppt}
          onClose={() => setCameraOpenAppt(null)}
          onUpload={uploadPhotos}
        />
      )}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      {uploadMenuOpen && (
        <div className={s.uploadPickerOverlay} onClick={() => setUploadMenuOpen(false)}>
          <div className={s.uploadPickerPanel} onClick={(e) => e.stopPropagation()}>
            <button className={s.uploadPickerItem} onClick={handlePickCamera}>Take Photo</button>
            <button className={s.uploadPickerItem} onClick={handlePickGallery}>Choose from Gallery</button>
            <button className={`${s.uploadPickerItem} ${s.uploadPickerCancel}`} onClick={() => setUploadMenuOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      <Header
        activeTab={visibleTab} setActiveTab={setActiveTab} shareDeviceOnly={shareDeviceOnly}
        lightMode={lightMode} toggleLightMode={toggleLightMode}
        time={time} lastFetch={lastFetch} isToday={isToday} selectedDate={selectedDate}
        onLogout={logout}
      />

      <StatsBar
        selectedDate={selectedDate} onChangeDate={setSelectedDate}
        allApptsCount={allAppts.length} totalDone={totalDone}
        byLocation={byLocation} activeLocations={activeLocations}
        toggleLocation={toggleLocation} isToday={isToday}
      />

      {visibleTab === 'queue' && (
        <div className={s.mainContent}>
          <div className={s.locationsGrid}>
            {activeLocations.length > 0
              ? Object.keys(LOCATION_META).filter(locId => activeLocations.includes(locId)).map(locId => (
                <LocationColumn key={locId} locId={locId} data={byLocation[locId]} date={selectedDate}
                  onToggleCheck={handleToggleCheck} onTogglePriority={handleTogglePriority}
                  loading={loading} staff={staff}
                  onAssignStaff={handleAssignStaff} onUnassignStaff={handleUnassignStaff}
                  dragOverId={dragOverId} setDragOverId={setDragOverId}
                  onUploadClick={handleUploadClick}
                  onViewPhotos={handleViewPhotos}
                  onNextStep={handleNextStep}
                  uploadStatuses={uploadStatuses}
                />
              )) : (<div className={s.noLocations}>No locations selected. Please select a location above to view appointments.</div>)
            }
          </div>
          <aside className={`${s.sidebarPanel} ${staffMinimized ? s.sidebarPanelMin : ''}`}>
            <div className={s.sidebarTopSection}>
              <div className={s.sidebarHeaderRow}>
                {!staffMinimized && <div className={s.sectionLabel}>STAFF</div>}
                <button
                  className={s.sidebarToggle}
                  onClick={toggleStaffMinimized}
                  title={staffMinimized ? 'Expand staff' : 'Minimize staff'}
                  aria-label={staffMinimized ? 'Expand staff panel' : 'Minimize staff panel'}
                >
                  {staffMinimized ? '«' : '»'}
                </button>
              </div>
              {!staffMinimized && <div className={s.sidebarHint}>Drag to assign · click status to change</div>}
            </div>
            {!staffMinimized && (
              <div className={s.sidebarStaffList}>
                {staff.map(member => <StaffMemberCard key={member.id} member={member} onCycleStatus={handleCycleStatus} />)}
              </div>
            )}
          </aside>
        </div>
      )}

      {visibleTab === 'analytics' && (
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <AnalyticsPanel light={lightMode} />
        </div>
      )}
    </div>
  );
}
